"use client"

import Link from "next/link"
import { ExternalLink } from "lucide-react"

import {
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu"
import { toast } from "@/hooks/use-toast"
import { ApiError } from "@/lib/api/client"
import { copyText } from "@/lib/clipboard"

import { useCurrentUser } from "@/features/auth/hooks"
import { canVoid } from "@/features/auth/role"

import { useResendInvoice, useSendInvoice } from "../hooks"
import { EMAIL_FAILED_STATUS, type BackendStatus } from "../types"
import { actionsForStatus } from "../actions"
import { payUrlFor, pdfUrlFor, portalUrlFor } from "../urls"

export interface InvoiceActionMenuProps {
  invoiceId: string
  invoiceNumber: string
  status: BackendStatus
  /** Used by canVoid to decide whether to show mutating items. */
  createdById?: number
  hubspotDealUrl?: string
}

export function InvoiceActionMenu({
  invoiceId,
  invoiceNumber,
  status,
  createdById,
  hubspotDealUrl,
}: InvoiceActionMenuProps) {
  const resendMutation = useResendInvoice()
  const sendMutation = useSendInvoice()
  const { data: user } = useCurrentUser()
  const allowMutating = canVoid(
    user,
    createdById !== undefined ? { created_by: { id: createdById } } : null,
  )
  const actions = actionsForStatus(status)
  // EMAIL_FAILED rows go through /send/ — see actions.ts comment.
  const isFirstSend = status === EMAIL_FAILED_STATUS
  const resendBusy = isFirstSend ? sendMutation.isPending : resendMutation.isPending

  const handleResend = () => {
    const onSuccess = () => {
      toast({
        // A never-delivered (EMAIL_FAILED) row is a first send, not a
        // resend — say "Email sent" to match the "Send email" label.
        title: isFirstSend ? "Email sent" : "Email resent",
        description: `Invoice #${invoiceNumber} was sent to the customer.`,
      })
    }
    const onError = (err: unknown) => {
      const detail =
        err instanceof ApiError
          ? (err.detail as { detail?: string } | undefined)?.detail
          : undefined
      toast({
        title: isFirstSend
          ? "Could not send invoice"
          : "Could not resend invoice",
        description: detail ?? "Please try again.",
        variant: "destructive",
      })
    }
    if (isFirstSend) {
      sendMutation.mutate({ id: invoiceId }, { onSuccess, onError })
    } else {
      resendMutation.mutate({ id: invoiceId }, { onSuccess, onError })
    }
  }

  const handleSendEmail = () => {
    sendMutation.mutate(
      { id: invoiceId },
      {
        onSuccess: () => {
          toast({
            title: "Email sent",
            description: `Invoice #${invoiceNumber} was sent to the customer.`,
          })
        },
        onError: (err: unknown) => {
          const detail =
            err instanceof ApiError
              ? (err.detail as { detail?: string } | undefined)?.detail
              : undefined
          toast({
            title: "Could not send invoice",
            description: detail ?? "Please try again.",
            variant: "destructive",
          })
        },
      },
    )
  }

  const handleCopyLink = async () => {
    const ok = await copyText(payUrlFor(invoiceId))
    toast(
      ok
        ? {
            title: "Payment link copied",
            description: "The link is now on your clipboard.",
          }
        : {
            title: "Could not copy",
            description: "Copy the URL from the address bar instead.",
            variant: "destructive",
          },
    )
  }

  const handleCopyPortalLink = async () => {
    const ok = await copyText(portalUrlFor(invoiceId))
    toast(
      ok
        ? {
            title: "Portal link copied",
            description:
              "Share it with the customer — they'll sign in with a one-time email link.",
          }
        : {
            title: "Could not copy",
            description: "Copy the URL from the address bar instead.",
            variant: "destructive",
          },
    )
  }

  return (
    <DropdownMenuContent align="end">
      <DropdownMenuItem asChild>
        <Link href={`/invoices/${invoiceId}`}>View</Link>
      </DropdownMenuItem>
      {actions.has("resend") && allowMutating && (
        <DropdownMenuItem
          onSelect={(event) => {
            event.preventDefault()
            handleResend()
          }}
          disabled={resendBusy}
        >
          {/* A never-delivered (EMAIL_FAILED) row was never emailed, so
              it offers "Send email" (a first send), not "Resend email". */}
          {isFirstSend ? "Send email" : "Resend email"}
        </DropdownMenuItem>
      )}
      {actions.has("sendEmail") && allowMutating && (
        // A `created` invoice was issued without emailing the customer;
        // this delivers the email and moves it to `sent` (routes to /send/,
        // like the EMAIL_FAILED first-send path).
        <DropdownMenuItem
          onSelect={(event) => {
            event.preventDefault()
            handleSendEmail()
          }}
          disabled={sendMutation.isPending}
        >
          Send email
        </DropdownMenuItem>
      )}
      {actions.has("amend") && allowMutating && (
        // Installment-plan invoices can't be amended (v1); the list row
        // doesn't carry plan info, so the amend modal + the backend (409)
        // are the authoritative gate for that case.
        <DropdownMenuItem asChild>
          <Link href={`/invoices/${invoiceId}/amend`}>Edit</Link>
        </DropdownMenuItem>
      )}
      {actions.has("reissue") && allowMutating && (
        <DropdownMenuItem asChild>
          <Link href={`/invoices/new?reissue_from=${invoiceId}`}>
            Reissue
          </Link>
        </DropdownMenuItem>
      )}
      {actions.has("copyLink") && (
        <DropdownMenuItem
          onSelect={(event) => {
            event.preventDefault()
            void handleCopyLink()
          }}
        >
          Copy payment link
        </DropdownMenuItem>
      )}
      {actions.has("copyPortalLink") && (
        <DropdownMenuItem
          onSelect={(event) => {
            event.preventDefault()
            void handleCopyPortalLink()
          }}
        >
          Copy portal link
        </DropdownMenuItem>
      )}
      {actions.has("downloadPdf") && (
        <DropdownMenuItem asChild>
          <a href={pdfUrlFor(invoiceId)} download>
            Download PDF
          </a>
        </DropdownMenuItem>
      )}
      {actions.has("void") && allowMutating && (
        <>
          <DropdownMenuSeparator />
          <DropdownMenuItem asChild className="text-destructive">
            <Link href={`/invoices/${invoiceId}/void`}>Void</Link>
          </DropdownMenuItem>
        </>
      )}
      {actions.has("delete") && allowMutating && (
        // Draft-only. ``allowMutating`` is ``canVoid`` == ``canDelete``
        // (not-Finance; SR must own; Manager any), so it doubles as the
        // delete ownership gate.
        <>
          <DropdownMenuSeparator />
          <DropdownMenuItem asChild className="text-destructive">
            <Link href={`/invoices/${invoiceId}/delete`}>Delete draft</Link>
          </DropdownMenuItem>
        </>
      )}
      {hubspotDealUrl && (
        <>
          <DropdownMenuSeparator />
          <DropdownMenuItem asChild>
            <a
              href={hubspotDealUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              View in HubSpot
              <ExternalLink className="h-3 w-3 ml-2" />
            </a>
          </DropdownMenuItem>
        </>
      )}
    </DropdownMenuContent>
  )
}
